import { type PropsWithChildren, type ReactNode } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { isMacOs } from "react-device-detect";
import { twMerge } from "tailwind-merge";

interface Props {
  isActive: boolean;
  isExpanded: boolean;
  icon: ReactNode;
  shortcut?: string;
}

export const NavLinkContent = ({
  isActive,
  isExpanded,
  icon,
  shortcut,
  children,
}: PropsWithChildren<Props>) => {
  return (
    <motion.div
      layout
      animate={{
        backgroundColor: isActive && isExpanded ? "#31405CFF" : "#31405C00",
      }}
      transition={{ duration: 0.1 }}
      className={clsx(
        "flex items-center gap-2",
        "text-sm text-white font-bold rounded-md",
        {
          "p-1.5 group": isExpanded,
        },
      )}
    >
      <motion.div
        initial={{ borderColor: "#E5E5E500" }}
        animate={{
          borderColor: isExpanded && !isActive ? "#E5E5E540" : "#E5E5E500",
        }}
        transition={{ duration: 0.1 }}
        className={twMerge(
          clsx(
            "w-9 h-9 shrink-0 flex items-center justify-center text-base border rounded",
            {
              "bg-cosmic hover:bg-v2-cosmic/600 group-hover:bg-v2-cosmic/600":
                isActive,
              "hover:text-v2-cosmic/400 hover:border-v2-gray/15/25%": !isActive,
              "group-hover:text-v2-cosmic/400 group-hover:border-v2-gray/15/25%":
                !isActive,
            },
          ),
        )}
      >
        {icon}
      </motion.div>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.1 }}
            className="w-full flex items-center justify-between"
          >
            <div>{children}</div>
            {shortcut && (
              <div
                className={clsx(
                  "text-xxs border rounded px-1 py-1",
                  "flex items-center",
                  {
                    "gap-[1px]": isMacOs,
                    "border-v2-gray/50/15%": !isActive,
                    "bg-white border-v2-gray/50 text-v2-interstellar/300":
                      isActive,
                  },
                )}
              >
                <span className="text-[9px]">
                  {isMacOs ? (
                    <i className="fa-regular fa-command"></i>
                  ) : (
                    "Ctrl+"
                  )}
                </span>
                {shortcut}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
